/**
 * PromoVideoEmbed — click-to-play YouTube embed for the CountBucks
 * promo video. 16:9 widescreen layout — the source is portrait but the
 * black side-bars read as intentional cinematic letterboxing inside a
 * dark-glow frame.
 */
import { useState } from 'react';
import { Play } from 'lucide-react';

const YOUTUBE_ID = 'eG9qzGv6kFE';

export default function PromoVideoEmbed({ className = '', large = false }) {
  const [playing, setPlaying] = useState(false);

  const handlePlay = () => {
    setPlaying(true);
    try {
      if (window.gtag) {
        window.gtag('event', 'video_play_started', {
          video_id: YOUTUBE_ID,
          surface: large ? 'demo_page' : 'landing_hero',
        });
      }
    } catch { /* no-op */ }
  };

  const maxW = large ? 'max-w-4xl' : 'max-w-3xl';

  return (
    <div className={`relative w-full ${maxW} mx-auto ${className}`} data-testid="promo-video-embed">
      {/* Outer halo — gradient bleeds into the black sidebars so they
          read as "part of the frame", not as YouTube letterboxing. */}
      <div className="absolute -inset-x-8 -inset-y-6 -z-10 rounded-3xl bg-gradient-to-br from-violet-600/30 via-fuchsia-500/15 to-emerald-500/20 blur-3xl opacity-70" />

      <div className="relative aspect-video rounded-2xl overflow-hidden border border-white/10 bg-zinc-950 shadow-2xl shadow-violet-900/40 ring-1 ring-violet-400/30">
        {!playing && (
          <button
            type="button"
            onClick={handlePlay}
            className="group absolute inset-0 w-full h-full"
            aria-label="Play CountBucks promo video"
            data-testid="promo-video-play-btn"
          >
            {/* Thumbnail — letterboxed YouTube returns a 16:9 thumbnail
                with the source video centered between black bars. We
                add a subtle violet glow overlay so the bars read as
                styled, not raw. */}
            <img
              src={`https://i.ytimg.com/vi/${YOUTUBE_ID}/maxresdefault.jpg`}
              onError={(e) => { e.currentTarget.src = `https://i.ytimg.com/vi/${YOUTUBE_ID}/hqdefault.jpg`; }}
              alt="CountBucks 60-second demo"
              className="w-full h-full object-cover opacity-95 group-hover:opacity-100 transition-opacity"
              loading="lazy"
            />
            {/* Color-grade overlay — adds a touch of violet to the black bars */}
            <span className="absolute inset-0 bg-gradient-to-br from-violet-900/15 via-transparent to-emerald-900/15" />
            <span className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-black/30" />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="flex items-center justify-center w-24 h-24 rounded-full bg-white/90 backdrop-blur shadow-2xl group-hover:scale-110 group-hover:bg-white transition-all">
                <Play className="w-10 h-10 text-black fill-black translate-x-[3px]" />
              </span>
            </span>
            {/* Bottom-left caption + duration pill */}
            <span className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-4 text-left">
              <span>
                <span className="block text-[10px] uppercase tracking-widest text-violet-300 font-mono">
                  60-second tour · with sound
                </span>
                <span className="block text-white text-base sm:text-lg font-semibold mt-1">
                  See CountBucks in action
                </span>
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs text-zinc-200 bg-black/50 backdrop-blur px-2.5 py-1 rounded-full font-mono">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse" />
                LIVE DEMO
              </span>
            </span>
          </button>
        )}
        {playing && (
          <iframe
            title="CountBucks promo"
            src={`https://www.youtube.com/embed/${YOUTUBE_ID}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            data-testid="promo-video-iframe"
          />
        )}
      </div>
    </div>
  );
}
